/**
 * Model_Router persistence — keep the per-model rate-limit counters
 * and cooldowns alive across page reloads so the app does not "forget"
 * that today's free-tier RPD has already been spent and walk straight
 * back into a 429 from the API.
 *
 * Stored shape (intentionally minimal — log entries are NOT persisted):
 *
 * ```
 * {
 *   schemaVersion: 1,
 *   savedAtMs: number,
 *   models: {
 *     [modelId]: {
 *       cooldownUntilMs: number,
 *       minuteTimestamps: number[],
 *       dayTimestamps: number[],
 *     }
 *   }
 * }
 * ```
 *
 * The minute/day timestamps are pruned on load against the current
 * wall clock so a tab reopened after midnight UTC starts with a
 * fresh-but-correct daily counter.
 *
 * This module is pure-ish: read/write are wrapped in try/catch so any
 * storage error (private mode, quota, etc.) silently degrades to
 * "no persistence" instead of breaking the app.
 */

import type { ModelId } from './types';
import { FALLBACK_CHAIN } from './fallbackChain';

const STORAGE_KEY = 'kiro.modelRouter.health.v1';
const SCHEMA_VERSION = 1;
const ONE_MINUTE_MS = 60_000;
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

export interface PersistedModelEntry {
  cooldownUntilMs: number;
  minuteTimestamps: number[];
  dayTimestamps: number[];
}

export interface PersistedRouterHealth {
  schemaVersion: number;
  savedAtMs: number;
  models: Partial<Record<ModelId, PersistedModelEntry>>;
}

function isModelId(value: unknown): value is ModelId {
  if (typeof value !== 'string') return false;
  for (const id of FALLBACK_CHAIN) {
    if (id === value) return true;
  }
  return false;
}

function isNumberArray(value: unknown): value is number[] {
  if (!Array.isArray(value)) return false;
  for (const n of value) {
    if (typeof n !== 'number' || !Number.isFinite(n)) return false;
  }
  return true;
}

function pruneOlderThan(times: number[], cutoff: number): number[] {
  const out: number[] = [];
  for (const t of times) {
    if (t >= cutoff) out.push(t);
  }
  return out;
}

/**
 * Load the persisted health snapshot, prune stale timestamps against
 * `nowMs`, and return a normalized record. Returns `null` if no
 * snapshot exists, the storage backend is unavailable, the payload
 * is corrupt, or the schema version does not match.
 */
export function loadPersistedHealth(
  nowMs: number,
): Partial<Record<ModelId, PersistedModelEntry>> | null {
  if (typeof localStorage === 'undefined') return null;
  let raw: string | null;
  try {
    raw = localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (raw === null || raw === '') return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw) as unknown;
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) return null;
  const obj = parsed as Record<string, unknown>;
  if (obj['schemaVersion'] !== SCHEMA_VERSION) return null;
  const models = obj['models'];
  if (typeof models !== 'object' || models === null) return null;

  const minuteCutoff = nowMs - ONE_MINUTE_MS;
  const dayCutoff = nowMs - ONE_DAY_MS;
  const out: Partial<Record<ModelId, PersistedModelEntry>> = {};

  for (const [modelKey, value] of Object.entries(models as Record<string, unknown>)) {
    if (!isModelId(modelKey)) continue;
    if (typeof value !== 'object' || value === null) continue;
    const entry = value as Record<string, unknown>;
    const cooldownUntilMs =
      typeof entry['cooldownUntilMs'] === 'number' &&
      Number.isFinite(entry['cooldownUntilMs'])
        ? (entry['cooldownUntilMs'] as number)
        : 0;
    const rawMinute = entry['minuteTimestamps'];
    const rawDay = entry['dayTimestamps'];
    const minuteTimestamps = isNumberArray(rawMinute)
      ? pruneOlderThan(rawMinute, minuteCutoff)
      : [];
    const dayTimestamps = isNumberArray(rawDay)
      ? pruneOlderThan(rawDay, dayCutoff)
      : [];
    out[modelKey] = {
      cooldownUntilMs: cooldownUntilMs > nowMs ? cooldownUntilMs : 0,
      minuteTimestamps,
      dayTimestamps,
    };
  }
  return out;
}

/**
 * Persist the current health snapshot. Best-effort — failures are
 * swallowed so a transient storage error never breaks the chat UI.
 */
export function savePersistedHealth(
  nowMs: number,
  models: Partial<Record<ModelId, PersistedModelEntry>>,
): void {
  if (typeof localStorage === 'undefined') return;
  const payload: PersistedRouterHealth = {
    schemaVersion: SCHEMA_VERSION,
    savedAtMs: nowMs,
    models,
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Quota exceeded, private mode, etc. — just drop the write.
  }
}

/**
 * Manually wipe persisted router health. Exposed so the diagnostics
 * panel can offer a "reset cooldowns" affordance.
 */
export function clearPersistedHealth(): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
